// Your routes — and the navigation binding's, which is the part worth knowing.
//
// THIS FILE IS REPLACED when the navigation shell is re-applied. `bottom-tabs`,
// `stack` and `sidebar` produce different routers, so the binding owns it and
// `appfactory spec apply` rewrites it with `overwrite: true`.
//
// It used to claim the opposite — "the factory never replaces it" — which was
// wrong and expensive: an apply silently took a TABS array full of hand-picked
// labels and icons with it, under a message saying four files were written.
// The apply now says loudly when it REPLACES rather than creates.
//
// So: edit it freely, and commit before running `spec apply`. Everything below
// is yours until the shell itself changes.

import { createRouter, createMemoryHistory } from '@ionic/vue-router';

import TabsShell from './pages/TabsShell.vue';

// The screens themselves come from ./product-routes.js, which is generated from
// .appfactory/product-plan.json by `appfactory product apply`.
//
// Two owners, one file each. THIS file is the shell — bottom-tabs, stack and
// sidebar produce different routers, and the binding rewrites it on every
// `spec apply`. The PRODUCT owns which screens exist. Before this split the
// shell hardcoded the worked example's three pages, so re-applying a shell
// silently deleted every screen the app had grown.
import { PRODUCT_ROUTES, DESTINATION_ROUTES, HOME_ROUTE } from './product-routes.js';

/**
 * A product path, made relative to the tab shell.
 *
 * The plan states routes ABSOLUTELY — `/`, `/tasks/:id` — because that is what
 * a screen's route means to everyone except vue-router. A child route with a
 * leading slash is mounted at the ROOT instead of under its parent, so the
 * shell wraps nothing and its outlet stays empty.
 */
const rel = (p) => String(p).replace(/^\/tabs\//, '').replace(/^\//, '');

/**
 * A tab's own path segment, and NEVER the empty string.
 *
 * A plan whose primary screen is routed at `/` produced `rel('/') === ''`, so
 * that screen's child route was `''` and its tab button's href was `/tabs/` —
 * the shell's OWN path. Ionic cannot reset a tab whose route is the parent it
 * lives inside: `resetTab()` in @ionic/vue-router calls `router.go()` to walk
 * back to that tab's root, and under `createMemoryHistory` the entry it wants is
 * not in the queue, so the location getter throws AND leaves the history
 * position out of range. Every navigation after that throws too, including
 * direct `router.replace()` calls.
 *
 * Measured before this fix, from a clean load: Lifts -> Bar worked once and the
 * app was then stuck on Bar forever, the route updating while the outlet did
 * not. Falling back to the screen's id gives every tab a distinct child path.
 */
const seg = (r) => rel(r.path) || r.id;

/**
 * Where the app opens. main.js pushes this.
 *
 * INSIDE the shell, deliberately. This was `HOME_ROUTE` — the raw plan path —
 * and an app whose primary screen is routed at `/` therefore generated
 * `{ path: '/', redirect: '/' }`. vue-router resolves that redirect, arrives at
 * the same route, resolves it again, and the app dies at boot with
 * `RangeError: Maximum call stack size exceeded`.
 *
 * That throw aborts the rest of main.js — before `router.isReady()`, before
 * `mount('#app')`, before `data-ready` — so `#app` stays empty on every route
 * and every store screenshot comes out blank. Nothing else reports it:
 * `npm run build` succeeds and check_app passed 7/7 against an app that
 * rendered nothing at all.
 *
 * A primary screen at `/` is the common case, and what set_plan encourages.
 */
export const HOME = `/tabs/${rel(HOME_ROUTE) || (DESTINATION_ROUTES.find((r) => r.path === HOME_ROUTE)?.id ?? '')}`;

/** Icons a generated tab falls back to, in order. Reorder or replace freely. */
// KEYED BY SCREEN ID, not by position. This was a positional array, so adding
// the Timeline screen in the middle of the plan silently handed Timeline the
// calculator and Bar the film reel. A tab's icon belongs to the screen, not to
// where it happens to sit in the bar.
//
// The app's own stroke icons (the design's `ICONS`), as the same kind of value
// an imported ionicon is: a `data:image/svg+xml;utf8,` string, which IonIcon
// parses in place without a fetch — so they are offline-safe for the reason
// given below. 24-unit grid, 1.5 stroke, drawn at 22px. The strokes sit on a
// `<g fill='none'>` because ion-icon sets `fill: currentColor` on its host and
// a rect or circle would otherwise render solid. Colour is `currentColor`, so
// the tab bar's active and inactive colours reach them.
const strokeIcon = (body) => "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'>"
  + `<g fill='none' stroke='currentColor' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'>${body}</g></svg>`;
const TAB_ICON = {
  lifts: strokeIcon("<path d='M2 12h20M5 8v8M8 6v12M16 6v12M19 8v8'/>"),
  bar: strokeIcon("<rect x='4' y='5' width='4' height='14' rx='1.2'/><rect x='9' y='7.5' width='3' height='9' rx='1.2'/><path d='M12 12h9M1.5 12H4'/>"),
  timeline: strokeIcon("<rect x='3' y='5' width='18' height='14' rx='2.5'/><path d='M10 9.5v5l4-2.5z'/>"),
  kit: strokeIcon("<circle cx='12' cy='12' r='8.5'/><circle cx='12' cy='12' r='2.5'/>"),
};
const TAB_ICONS = [barbellOutline, calculatorOutline, filmOutline];  // fallback order

// The tabs, in bar order, with the icon each one wears.
//
// `icon` is an IMPORTED ionicon, not a name. `<ion-icon name="home">` fetches
// its SVG from a CDN at runtime, which the app's content-security-policy
// blocks and an offline app could not reach anyway — you get an invisible tab
// bar that works fine on your machine and is blank on a plane.
//
// Every tab is a route. A tab bar is for DESTINATIONS — if your app's main verb
// needs a permanent home, give it a real screen; if it is an action, it belongs
// on the screen it acts on, the way "Add an item" does on Home.
import { barbellOutline, calculatorOutline, filmOutline } from 'ionicons/icons';

export const TABS = [
  // DESTINATIONS, not PRIMARY_ROUTES.
  //
  // Exactly one screen is `primary` — it names where the app opens — so a tab
  // bar built from it has exactly one tab, and every other top-level screen is
  // unreachable. A parameterised screen marked primary made it worse: the tab
  // carried `href="/recipe/:recipeId/capture"`, a literal `:` in a path that
  // can never resolve. `destination` is the question a tab bar is asking, and
  // it already excludes detail screens and settings.
  ...DESTINATION_ROUTES.map((r, i) => ({
    name: r.id,
    label: r.label,
    // Shell-prefixed, like every other link into the product: a tab pointing at
    // the bare plan path navigates out of the shell and loses the tab bar.
    path: `/tabs/${seg(r)}`,
    // A tab without an icon renders as a label floating in empty space, so
    // there is always one. Change them freely — this array is yours.
    icon: TAB_ICON[r.id] ?? TAB_ICONS[i % TAB_ICONS.length],
  })),
];

/**
 * A memory history that numbers its entries, the way the browser's does.
 *
 * @ionic/vue-router keeps each tab's stack by reading `history.state.position`
 * (and `.replaced`) after every navigation. vue-router's WEB history writes
 * those into every entry; its MEMORY history keeps only what it is handed and
 * never does. So under memory history every entry's position was `undefined`,
 * and tapping the tab you were already in — Lifts, from a Ramp pushed inside
 * it — ran Ionic's `resetTab()`, which is `router.go(first.position - current)`
 * = `router.go(NaN)`. Memory history clamps a number but not NaN: its own
 * position became NaN, the location getter read `queue[NaN][0]`, and the app
 * threw `Cannot read properties of undefined (reading '0')`. The tab did
 * nothing, and every navigation after it threw the same way — Back from the
 * Ramp included, and the Bar tab moved the route but not the screen.
 *
 * The two earlier fixes (`seg` above, and main.js's `replace(HOME)`) are this
 * missing number seen from other angles. This supplies it exactly as web
 * history's `buildState` does — a push is the current entry's position + 1, a
 * replace keeps it and says it replaced — and refuses a non-finite `go()`
 * rather than letting it poison the position.
 *
 * THIS FILE IS REPLACED by `spec apply` (see the top): carry this across.
 */
function numberedMemoryHistory() {
  const history = createMemoryHistory();
  const at = () => Number(history.state?.position) || 0;
  const push = history.push.bind(history);
  const replace = history.replace.bind(history);
  const go = history.go.bind(history);
  history.push = (to, state) => push(to, { ...state, position: at() + 1, replaced: false });
  history.replace = (to, state) => replace(to, { ...state, position: at(), replaced: true });
  history.go = (delta, trigger) => {
    if (Number.isFinite(delta)) go(delta, trigger);
  };
  return history;
}

export default createRouter({
  history: numberedMemoryHistory(),
  routes: [
    { path: '/', redirect: HOME },
    {
      // The tab shell owns its own outlet, so each tab keeps its own
      // navigation stack. Pushing /tabs/items/3 leaves the tab bar in place
      // and the back button working, and switching away and back returns you
      // to the detail screen rather than the top of the list.
      path: '/tabs/',
      component: TabsShell,
      // The empty child is NOT a redirect. Once paths are relative the home
      // screen occupies that slot itself, and sending it onward to a route that
      // resolves back here is the loop described on HOME above.
      children: [
        // `props: true` on every route, so a screen with `:id` in its path gets
        // it as a prop as well as through useRoute(). A detail screen that has
        // to reach for the router to learn which record it is showing is a
        // screen that cannot be tested without one.
        ...PRODUCT_ROUTES.map((r) => ({
          path: seg(r),
          name: r.id,
          component: r.component,
          props: true,
        })),
      ],
    },
  ],
});
